import type { InputHTMLAttributes } from "react"

import {
  useFormContext,
  type FieldPath,
  type FieldValues,
} from "react-hook-form"
import { NumericFormat } from "react-number-format"

import { FieldInfo } from "@/components/ui/field-info"
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

type CustomFormInputNumericProps<T extends FieldValues = FieldValues> = {
  name: FieldPath<T>
  label?: string
  // Explanation the label itself is too long to carry, shown behind an "i".
  info?: string
  placeholder: string
  className?: string
  inputClassName?: string
  helpText?: string
  disabled?: boolean
  allowDecimals?: boolean
  decimalScale?: number
  allowNegative?: boolean
  setValueAsString?: boolean
  prefix?: string
}

export type FormInputNumericProps<T extends FieldValues = FieldValues> =
  CustomFormInputNumericProps<T> &
    Omit<
      InputHTMLAttributes<HTMLInputElement>,
      | "name"
      | "value"
      | "onChange"
      | "onBlur"
      | "ref"
      | "defaultValue"
      | "type"
      | "label"
      | "placeholder"
      | "className"
      | "disabled"
      | "prefix"
    >

export function FormInputNumeric<T extends FieldValues = FieldValues>({
  name,
  label,
  info,
  placeholder,
  className,
  inputClassName,
  helpText,
  disabled = false,
  allowDecimals = false,
  decimalScale = 2,
  allowNegative = false,
  setValueAsString = false,
  prefix,
  ...otherHtmlAttributes
}: FormInputNumericProps<T>) {
  const form = useFormContext<T>()

  return (
    <FormField
      control={form.control}
      name={name}
      disabled={disabled}
      render={({ field }) => (
        <FormItem className={cn("w-full", className)}>
          {label && (
            <FormLabel>
              {label}
              {info && <FieldInfo text={info} />}
            </FormLabel>
          )}
          <FormControl>
            <NumericFormat
              customInput={Input}
              value={field.value ?? ""}
              placeholder={placeholder}
              className={inputClassName}
              disabled={disabled || field.disabled}
              prefix={prefix}
              thousandSeparator=","
              decimalScale={allowDecimals ? decimalScale : 0}
              allowNegative={allowNegative}
              inputMode={allowDecimals ? "decimal" : "numeric"}
              {...otherHtmlAttributes}
              onValueChange={(values) => {
                if (values.value === "") {
                  field.onChange("")
                  return
                }

                field.onChange(
                  setValueAsString ? values.value : (values.floatValue ?? ""),
                )
              }}
              onBlur={field.onBlur}
              name={field.name}
              getInputRef={field.ref}
            />
          </FormControl>
          {helpText && <FormDescription>{helpText}</FormDescription>}
          <FormMessage className="form-error" />
        </FormItem>
      )}
    />
  )
}
